// Etapas de uso da Gestão de Estoque — fonte única da tela de Boas-vindas (tutorial) e da
// navegação lateral, que é agrupada e ordenada por estas mesmas etapas. `roles` restringe a
// tela (ex.: relatório, fechamento e dashboard são do perfil Financeiro).
import {
  ArrowDownToLine, ArrowUpFromLine, BarChart3, Boxes, ClipboardCheck, ClipboardList, History, Home,
  LayoutDashboard, ListChecks, Lock, Package, SlidersHorizontal,
} from "lucide-react";
import { ROLES_ESTOQUE_FINANCEIRO } from "../../constants/estoque";

export const TELA_BOAS_VINDAS = {
  id: "boas-vindas", label: "Boas-vindas", path: "/estoque/boas-vindas", icon: Home, description: "Passo a passo de uso do módulo",
};

export const ETAPAS_ESTOQUE = [
  {
    numero: 1,
    id: "preparar",
    titulo: "Preparar",
    resumo: "Confira a lista de produtos e cadastre os fornecedores.",
    passos: [
      "Revise os produtos: cada um tem UMA unidade padrão (litro, rolo, unidade…). Toda quantidade fica nela.",
      "Defina, se já souber, estoque mínimo, máximo e consumo planejado por mês — eles alimentam os alertas.",
      "Cadastre os fornecedores com CNPJ (dá para cadastrar também na hora de lançar a NF).",
    ],
    telas: [
      { id: "cadastros", label: "Cadastros", path: "/estoque/cadastros", icon: Boxes, description: "Produtos, categorias, unidades e fornecedores" },
      { id: "parametros", label: "Parâmetros", path: "/estoque/parametros", icon: SlidersHorizontal, description: "Tolerâncias e limites dos insights" },
    ],
  },
  {
    numero: 2,
    id: "abrir",
    titulo: "Abrir o estoque",
    resumo: "Conte tudo o que existe hoje e dê valor a cada item. Acontece uma única vez.",
    passos: [
      "Inicie a vistoria e percorra o estoque com o celular: digite a quantidade de cada produto e toque em ✓ (0 = não tem).",
      "Produto que não está na lista? Cadastre ali mesmo, sem sair da contagem.",
      "O financeiro informa o valor unitário de cada item (à mão ou pela NF mais recente) e conclui a abertura.",
    ],
    telas: [
      { id: "vistoria", label: "Vistoria de Abertura", path: "/estoque/vistoria", icon: ClipboardCheck, description: "Contagem inicial e valorização do estoque" },
    ],
  },
  {
    numero: 3,
    id: "movimentar",
    titulo: "Movimentar no dia a dia",
    resumo: "Toda entrada vem de uma nota fiscal; toda retirada é uma saída.",
    passos: [
      "Chegou mercadoria: lance a NF com os itens. Se vier em fardo/caixa, informe o fator (ex.: 1 fardo = 12 rolos).",
      "Retirou material para uso: registre a saída (vários produtos de uma vez, pelo celular).",
      "Achou diferença, perda ou avaria: lance um ajuste com o motivo. Nada é apagado — lançamentos errados são cancelados.",
    ],
    telas: [
      { id: "notas-fiscais", label: "Entradas (NF)", path: "/estoque/notas-fiscais", icon: ArrowDownToLine, description: "Entrada de mercadoria por nota fiscal" },
      { id: "saidas", label: "Saídas", path: "/estoque/saidas", icon: ArrowUpFromLine, description: "Retirada de material para uso" },
      { id: "movimentacoes", label: "Lançamentos", path: "/estoque/movimentacoes", icon: ListChecks, description: "Todas as movimentações, ajustes e cancelamentos" },
    ],
  },
  {
    numero: 4,
    id: "fechar",
    titulo: "Conferir e fechar o mês",
    resumo: "Conte o estoque, corrija as diferenças e feche o período.",
    passos: [
      "No fim do mês, faça o inventário: a contagem é às cegas (sem ver o saldo do sistema).",
      "O financeiro confere as diferenças e conclui — cada diferença vira um ajuste automático.",
      "Feche o mês: a tela mostra as pendências antes. Mês fechado não muda mais.",
    ],
    telas: [
      { id: "inventarios", label: "Inventário Periódico", path: "/estoque/inventarios", icon: ClipboardList, description: "Contagem física e ajustes de diferença" },
      { id: "fechamento", label: "Fechamento", path: "/estoque/fechamento", icon: Lock, description: "Fechar o mês e gravar o snapshot dos saldos", roles: ROLES_ESTOQUE_FINANCEIRO },
    ],
  },
  {
    numero: 5,
    id: "acompanhar",
    titulo: "Acompanhar",
    resumo: "Saldo, histórico de cada produto, relatório do mês e alertas.",
    passos: [
      "Posição de estoque: quanto tem de cada produto agora, em unidades e R$.",
      "Extrato do produto: cada entrada e saída com o saldo e o custo médio linha a linha.",
      "Relatório e dashboard: entradas × consumo, curva ABC e alertas (falta, compra em excesso, preço subindo…).",
    ],
    telas: [
      { id: "posicao", label: "Posição de Estoque", path: "/estoque/posicao", icon: Package, description: "Saldo atual por produto em unidades e R$" },
      { id: "kardex", label: "Extrato do Produto", path: "/estoque/kardex", icon: History, description: "Kardex com saldo acumulado e custo médio" },
      { id: "relatorio", label: "Movimentação do Período", path: "/estoque/relatorio", icon: BarChart3, description: "Saldo inicial, entradas, saídas, ajustes e final", roles: ROLES_ESTOQUE_FINANCEIRO },
      { id: "dashboard", label: "Dashboard e Insights", path: "/estoque/dashboard", icon: LayoutDashboard, description: "Indicadores, gráficos e alertas do período", roles: ROLES_ESTOQUE_FINANCEIRO },
    ],
  },
];

/** Etapas com só as telas que a role pode abrir (etapa sem tela visível some). */
export function etapasVisiveis(role) {
  return ETAPAS_ESTOQUE
    .map((e) => ({ ...e, telas: e.telas.filter((t) => !t.roles || t.roles.includes(role)) }))
    .filter((e) => e.telas.length > 0);
}
